import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import {
  PuiTextField,
  type Question as QuestionType,
  type QuestionAnswer,
  QuestionAnswerTypes,
} from '@pbt/pbt-ui-components'
import { findConstantIdByName } from '@pbt/pbt-ui-components/src/utils'

import { getAnswerInputTypes } from '~/store/reducers/constants'

interface QuantitySelectorOptionsProps {
  disableAnswer: boolean
  questionCandidate: {
    answers?: QuestionAnswer[]
  }
  setQuestionCandidate: (candidate: Partial<QuestionType>) => void
}

type ExtendedQuestionAnswer = QuestionAnswer & {
  quantityMax?: number
  quantityMin?: number
}

const QuantitySelectorOptions: FC<QuantitySelectorOptionsProps> = ({
  disableAnswer,
  questionCandidate,
  setQuestionCandidate,
}) => {
  const { t } = useTranslation(['Admin'])
  const AnswerInputTypes = useSelector(getAnswerInputTypes)

  const currentAnswer = questionCandidate?.answers?.[0] as
    | ExtendedQuestionAnswer
    | undefined

  const handleQuantityChange = (updatedAnswer: ExtendedQuestionAnswer) => {
    const inputTypeId = findConstantIdByName(
      QuestionAnswerTypes.QUANTITY_SELECTOR,
      AnswerInputTypes,
    )

    const finalAnswer = {
      ...updatedAnswer,
      inputs: [
        {
          ...currentAnswer?.inputs?.[0],
          inputTypeId,
        },
      ],
    } as ExtendedQuestionAnswer

    setQuestionCandidate({
      ...questionCandidate,
      answers: [finalAnswer],
    } as Partial<QuestionType>)
  }

  return (
    <Grid container pl={4.5}>
      <Grid item pr={2} xs={5}>
        <PuiTextField
          disabled={disableAnswer}
          label={t('Admin:CATALOG.QUESTION.MINIMUM_NUMBER')}
          type="number"
          value={currentAnswer?.quantityMin ?? 0}
          onChange={(e) => {
            const quantityMin = Number(e.target.value)
            handleQuantityChange({
              ...currentAnswer,
              quantityMin,
              quantityMax: currentAnswer?.quantityMax ?? 0,
            } as ExtendedQuestionAnswer)
          }}
        />
      </Grid>
      <Grid item pl={2} xs={5}>
        <PuiTextField
          disabled={disableAnswer}
          label={t('Admin:CATALOG.QUESTION.MAXIMUM_NUMBER')}
          type="number"
          value={currentAnswer?.quantityMax ?? 0}
          onChange={(e) => {
            const quantityMax = Number(e.target.value)
            handleQuantityChange({
              ...currentAnswer,
              quantityMin: currentAnswer?.quantityMin ?? 0,
              quantityMax,
            } as ExtendedQuestionAnswer)
          }}
        />
      </Grid>
    </Grid>
  )
}

export default QuantitySelectorOptions
